import React, {
  FunctionComponent,
  useContext,
  useState,
  useRef,
  memo
} from 'react';
import 'gif-picker-react-klipy/styles.css';
import giphyStyles from '../EmojiPicker/styles.module.scss';
import { Icon } from 'components/Icons';
import { useOnClickOutside } from 'usehooks-ts';
import { ThemeContext } from 'features/theme/theme';
import { useAppSelector } from 'hooks/redux';
import { GifPicker } from 'gif-picker-react-klipy/index';
import type { GifImage } from 'gif-picker-react-klipy';
import { usePopper } from 'react-popper';
import { UserAgentContext } from 'features/user-agent/userAgent';

interface GiphyPickerProps {
  onGifClick: (gif: GifImage) => void;
}

export const GiphyPicker: FunctionComponent<GiphyPickerProps> = (props) => {
  const [pickerButton, setPickerButton] = useState<HTMLButtonElement | null>(
    null
  );
  const { isNativeAppWebview, isDACreatorApp } = useContext(UserAgentContext);
  const [pickerGiphy, setPickerGiphy] = useState<HTMLDivElement | null>(null);
  const giphyPickerRef = useRef<HTMLDivElement>(null);
  const [pickerOpen, togglePicker] = useState<boolean>(false);
  const { darkMode } = useContext(ThemeContext);
  const gifsInChatEnabled = useAppSelector(
    (state) => state.channel.channelDetails?.gifsInChatEnabled
  );
  const { styles, attributes } = usePopper(pickerButton, pickerGiphy, {
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          padding: isNativeAppWebview ? 16 : 8,
          rootBoundary: 'document'
        }
      }
    ],
    strategy: 'fixed'
  });

  const daCSS = isDACreatorApp
    ? '[&_.gpr-gif-item]:!tw-transition-none [&_.gpr-category-item]:!tw-transition-none [&_.gpr-gif-column]:tw-p-2 [&_.gpr-gif-column]:tw-w-1/2'
    : '';

  const handleGifPickerClickOutside = () => {
    togglePicker(false);
  };

  useOnClickOutside(giphyPickerRef, handleGifPickerClickOutside);

  const onGifClick = (gif: GifImage) => {
    props.onGifClick(gif);
    togglePicker(false);
  };

  if (!gifsInChatEnabled) {
    return null;
  }

  return (
    <div ref={giphyPickerRef}>
      <button
        ref={setPickerButton}
        onClick={() => togglePicker(!pickerOpen)}
        className={giphyStyles['emoji-picker__button']}
        type="button"
      >
        <Icon icon={'gif'} hidden={true} label={'Choose gif'} />
      </button>
      {pickerOpen && (
        <div
          className={`tw-shadow-xl tw-rounded-xl tw-overflow-hidden tw-z-50 tw-max-w-[280px] w-full [&_.gpr-trending-terms]:tw-hidden [&_.gpr-trending-terms]:tw-p-0 [&_.gpr-trending-terms]:tw-m-0 [&_.gpr-categories-section]:mt-0 ${daCSS}`}
          ref={setPickerGiphy}
          style={styles.popper}
          {...attributes.popper}
        >
          <GifPicker
            klipyApiKey={`${import.meta.env.VITE_KLIPY_API_KEY}`}
            autoFocusSearch={false}
            onGifClick={onGifClick}
            theme={darkMode ? 'dark' : 'light'}
            contentFilter={'medium'}
            height={260}
            width={'100%'}
          />
        </div>
      )}
    </div>
  );
};

export default memo(GiphyPicker);
